import React, { useState, useEffect } from 'react';
import { X, Shield, CheckCircle2, Loader2 } from 'lucide-react';
import { ref, get } from 'firebase/database';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { database, db } from '../../firebase';
import '../../styles/CursosVortex.css';

// Modal de perfil Vortex Academy: selecciona jerarquía, cuerpo/servicio y especialidad
// desde app_metadata y lo guarda en Firestore users/{uid}.
export const VortexProfileModal = ({ isOpen, onClose, onSaved, uid }) => {
  const [metadata, setMetadata] = useState({ cuerpos: [], especialidades: {}, grados: [] });
  const [jerarquia, setJerarquia] = useState('');
  const [cuerpoServicio, setCuerpoServicio] = useState('');
  const [especialidad, setEspecialidad] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    get(ref(database, 'app_metadata'))
      .then((snapshot) => {
        if (cancelled) return;
        if (snapshot.exists()) {
          const data = snapshot.val();
          setMetadata({
            cuerpos: data.cuerpos_y_servicios ? Object.values(data.cuerpos_y_servicios) : [],
            especialidades: data.especialidades || {},
            grados: data.grados ? Object.values(data.grados) : [],
          });
        }
      })
      .catch((e) => setError('No se pudo cargar el catálogo de perfiles: ' + e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [isOpen]);

  const handleSave = async () => {
    if (!jerarquia || !cuerpoServicio) {
      setError('Selecciona al menos jerarquía y cuerpo/servicio.');
      return;
    }
    if (!uid) {
      setError('Debes iniciar sesión para configurar tu perfil.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await setDoc(doc(db, 'users', uid), {
        jerarquia,
        grado: jerarquia,
        cuerpoServicio,
        especialidad,
        academyProfile: true,
        lastOperationalUpdate: serverTimestamp(),
      }, { merge: true });
      if (onSaved) onSaved({ jerarquia, cuerpoServicio, especialidad });
      onClose();
    } catch (e) {
      setError('Error al guardar el perfil: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="vx-modal-overlay" onClick={() => !saving && onClose()}>
      <div className="vx-modal-card" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid rgba(197,160,89,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #0f2035 0%, #0a1a2a 100%)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '38px', height: '38px', borderRadius: '10px',
              background: 'rgba(197,160,89,0.15)', border: '1px solid rgba(197,160,89,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold-matte)',
            }}>
              <Shield size={18} />
            </div>
            <div>
              <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', margin: 0 }}>
                VORTEX ACADEMY
              </p>
              <h3 style={{ fontFamily: 'var(--font-tactical)', fontSize: '0.95rem', color: '#fff', margin: 0 }}>
                Configura tu Perfil Operativo
              </h3>
            </div>
          </div>
          <button className="btn-vx-modal-close" onClick={onClose} disabled={saving}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto' }}>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            Según tu <strong style={{ color: '#fff' }}>jerarquía, cuerpo/servicio y especialidad</strong> se
            te presentarán los cursos, cuestionarios y material de estudio de Vortex Academy.
          </p>

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '2rem 0', color: 'var(--text-muted)' }}>
              <Loader2 className="w-5 h-5" style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '0.8rem' }}>Cargando catálogo de perfiles...</span>
            </div>
          ) : (
            <>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 700, color: '#c5a059', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px', display: 'block' }}>
                  Jerarquía / Grado *
                </label>
                <select
                  value={jerarquia}
                  onChange={(e) => setJerarquia(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '9px',
                    background: '#0a1a2a', border: '1px solid rgba(197,160,89,0.3)',
                    color: '#e2e8f0', fontSize: '0.8rem', outline: 'none',
                  }}
                >
                  <option value="">Seleccionar jerarquía...</option>
                  {metadata.grados.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 700, color: '#c5a059', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px', display: 'block' }}>
                  Cuerpo / Servicio *
                </label>
                <select
                  value={cuerpoServicio}
                  onChange={(e) => { setCuerpoServicio(e.target.value); setEspecialidad(''); }}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '9px',
                    background: '#0a1a2a', border: '1px solid rgba(197,160,89,0.3)',
                    color: '#e2e8f0', fontSize: '0.8rem', outline: 'none',
                  }}
                >
                  <option value="">Seleccionar cuerpo/servicio...</option>
                  {metadata.cuerpos.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 700, color: '#c5a059', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '5px', display: 'block' }}>
                  Especialidad
                </label>
                <select
                  value={especialidad}
                  onChange={(e) => setEspecialidad(e.target.value)}
                  disabled={!cuerpoServicio || !metadata.especialidades[cuerpoServicio]?.length}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: '9px',
                    background: '#0a1a2a', border: '1px solid rgba(197,160,89,0.3)',
                    color: '#e2e8f0', fontSize: '0.8rem', outline: 'none',
                    opacity: (!cuerpoServicio || !metadata.especialidades[cuerpoServicio]?.length) ? 0.5 : 1,
                  }}
                >
                  <option value="">Seleccionar especialidad...</option>
                  {(metadata.especialidades[cuerpoServicio] || []).map((esp) => <option key={esp} value={esp}>{esp}</option>)}
                </select>
              </div>

              {error && (
                <div style={{
                  padding: '10px 12px', borderRadius: '9px', textAlign: 'center',
                  background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)', color: '#fecaca', fontSize: '0.75rem',
                }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem', borderTop: '1px solid rgba(197,160,89,0.2)',
          background: '#0a1a2a', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', flexShrink: 0,
        }}>
          <button className="btn-card-resumen" onClick={onClose} disabled={saving} style={{ padding: '10px 18px', fontSize: '0.78rem' }}>
            Cancelar
          </button>
          <button className="btn-vx-action" onClick={handleSave} disabled={saving || loading} style={{ padding: '10px 22px', gap: '8px' }}>
            {saving ? <Loader2 className="w-4 h-4" style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle2 size={16} />}
            <span>{saving ? 'Guardando...' : 'Guardar y Continuar'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
